import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Input } from "./input";
import { KeyboardArrowDown as ChevronDown } from "@mui/icons-material";

interface Country {
  code: string;
  name: string;
  dial: string;
  flag: string;
}

const countries: Country[] = [
  { code: "BR", name: "Brasil", dial: "+55", flag: "🇧🇷" },
  { code: "US", name: "Estados Unidos", dial: "+1", flag: "🇺🇸" },
  { code: "PT", name: "Portugal", dial: "+351", flag: "🇵🇹" },
  { code: "AR", name: "Argentina", dial: "+54", flag: "🇦🇷" },
  { code: "CL", name: "Chile", dial: "+56", flag: "🇨🇱" },
  { code: "CO", name: "Colômbia", dial: "+57", flag: "🇨🇴" },
  { code: "MX", name: "México", dial: "+52", flag: "🇲🇽" },
  { code: "UY", name: "Uruguai", dial: "+598", flag: "🇺🇾" },
  { code: "PY", name: "Paraguai", dial: "+595", flag: "🇵🇾" },
  { code: "PE", name: "Peru", dial: "+51", flag: "🇵🇪" },
  { code: "BO", name: "Bolívia", dial: "+591", flag: "🇧🇴" },
  { code: "EC", name: "Equador", dial: "+593", flag: "🇪🇨" },
  { code: "VE", name: "Venezuela", dial: "+58", flag: "🇻🇪" },
  { code: "ES", name: "Espanha", dial: "+34", flag: "🇪🇸" },
  { code: "FR", name: "França", dial: "+33", flag: "🇫🇷" },
  { code: "DE", name: "Alemanha", dial: "+49", flag: "🇩🇪" },
  { code: "IT", name: "Itália", dial: "+39", flag: "🇮🇹" },
  { code: "GB", name: "Reino Unido", dial: "+44", flag: "🇬🇧" },
  { code: "CA", name: "Canadá", dial: "+1", flag: "🇨🇦" },
  { code: "JP", name: "Japão", dial: "+81", flag: "🇯🇵" },
  { code: "CN", name: "China", dial: "+86", flag: "🇨🇳" },
  { code: "IN", name: "Índia", dial: "+91", flag: "🇮🇳" },
  { code: "AU", name: "Austrália", dial: "+61", flag: "🇦🇺" },
  { code: "ZA", name: "África do Sul", dial: "+27", flag: "🇿🇦" },
  { code: "AO", name: "Angola", dial: "+244", flag: "🇦🇴" },
  { code: "MZ", name: "Moçambique", dial: "+258", flag: "🇲🇿" },
];

const formatLocalPhone = (value: string, dialCode: string) => {
  const digits = value.replace(/\D/g, "");
  if (dialCode === "+55") {
    if (digits.length === 0) return "";
    if (digits.length <= 2) return `${digits}`;
    if (digits.length <= 7) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    return `${digits.slice(0, 2)} ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  }
  return digits;
};

interface PhoneInputProps {
  id: string;
  value: string;
  onChange: (fullNumber: string) => void;
  background?: string;
  placeholder?: string;
}

const PhoneInputWithCountry = ({
  id,
  value,
  onChange,
  placeholder = "11 99999-9999",
  background,
  ...props
}: PhoneInputProps) => {
  const [selectedCountry, setSelectedCountry] = useState<Country>(countries[0]);
  const [localNumber, setLocalNumber] = useState("");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!value) {
      setLocalNumber("")
      return
    }

    // Find matching country by dial code
    const country = countries.find(c => value.startsWith(c.dial))

    if (country) {
      setSelectedCountry(country)

      const local = value.slice(country.dial.length)
      setLocalNumber(formatLocalPhone(local, country.dial))
    }
  }, [value])

  // Fecha ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      ) return;
      setOpen(false);
      setSearch("");
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Recalcula posição ao scrollar ou redimensionar
  useEffect(() => {
    if (!open) return;
    const update = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    };
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open]);

  const handleOpen = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
      width: rect.width,
    });
    setOpen((prev) => !prev);
  };

  const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatLocalPhone(e.target.value, selectedCountry.dial);
    setLocalNumber(formatted);
    onChange(`${selectedCountry.dial}${formatted.replace(/\D/g, "")}`);
  };

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setOpen(false);
    setSearch("");
    onChange(`${country.dial}${localNumber.replace(/\D/g, "")}`);
  };

  const filtered = countries.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.dial.includes(search) ||
      c.code.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="relative flex">
      {/* Country selector */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleOpen}
        className="flex items-center bg-gray-50 gap-1 px-3 h-10 rounded-l-lg border border-r-0 border-gray-200 text-sm hover:bg-gray-50 transition-colors shrink-0"
        style={background ? { background } : {}}
      >
        <span className="text-lg leading-none">{selectedCountry.flag}</span>
        <span className="text-gray-700 font-medium">{selectedCountry.dial}</span>
        <ChevronDown sx={{ fontSize: 16 }} className="text-gray-400" />
      </button>

      {/* Dropdown via Portal */}
      {open && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: "absolute",
            top: coords.top,
            left: coords.left,
            width: 288,
            zIndex: 9999,
          }}
          className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
        >
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              placeholder="Buscar país..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 bg-white placeholder:text-gray-300 outline-none focus:border-gray-300"
              autoFocus
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {filtered.map((country) => (
              <button
                key={country.code}
                type="button"
                onClick={() => handleCountrySelect(country)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
                  country.code === selectedCountry.code ? "bg-gray-100" : ""
                }`}
              >
                <span className="text-lg leading-none">{country.flag}</span>
                <span className="text-gray-700 flex-1 text-left">{country.name}</span>
                <span className="text-gray-400">{country.dial}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">
                Nenhum país encontrado
              </p>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Phone input */}
      <Input
        id={id}
        className="rounded-l-none rounded-r-lg px-3 py-2.5 border-gray-200 text-sm placeholder:text-gray-300 focus-visible:ring-0 focus-visible:border-gray-300"
        placeholder={placeholder}
        value={localNumber}
        onChange={handleLocalChange}
        style={background ? { background } : {}}
        {...props}
      />
    </div>
  );
};

export default PhoneInputWithCountry;